import { Icon } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ValidateTranscriptionCopy } from './validate-transcription-copy';
import { TRANSCRIPTION_REJECTION_REASONS } from './validate-transcription.session';

type ValidateTranscriptionDecisionPanelProps = {
  t: Record<string, unknown>;
  copy: ValidateTranscriptionCopy;
  text: string;
  onTextChange: (value: string) => void;
  onFinalize: () => void;
  rejectionReason: string;
  onRejectionReasonChange: (value: string) => void;
  detailedRejectionReason: string;
  onDetailedRejectionReasonChange: (value: string) => void;
  isLoading: boolean;
  isSubmitting: boolean;
};

/**
 * The proposed text, editable before validating, the finalize action, and
 * what a rejection needs (a reason, optional details). Validate and reject
 * themselves sit in the action bar.
 */
export function ValidateTranscriptionDecisionPanel({
  t,
  copy,
  text,
  onTextChange,
  onFinalize,
  rejectionReason,
  onRejectionReasonChange,
  detailedRejectionReason,
  onDetailedRejectionReasonChange,
  isLoading,
  isSubmitting,
}: ValidateTranscriptionDecisionPanelProps) {
  const s = (key: string) => String(t[key] ?? '');
  return (
    <section
      className="space-y-5 rounded-xl border border-border/60 bg-surface p-5 sm:p-6"
      data-testid="validate-transcribe-card"
      aria-label={s('validateThisText')}
    >
      <div className="space-y-2">
        <label htmlFor="validate-transcription-text" className="text-sm font-medium text-foreground">
          {s('validateThisText')}
        </label>
        <Textarea
          id="validate-transcription-text"
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={s('correctionPlaceholder')}
          disabled={isLoading}
          className="min-h-[140px] bg-surface text-base leading-7"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onFinalize}
            disabled={isSubmitting}
            className="h-11"
            data-testid="validate-transcription-finalize"
          >
            {isSubmitting ? <Icon name="loader2" className="size-4 animate-spin" /> : <Icon name="save" className="size-4" />}
            {copy.finalize}
          </Button>
        </div>
      </div>

      <div className="space-y-2 border-t border-border/60 pt-5">
        <p className="text-sm font-medium text-foreground">{copy.rejectTitle}</p>
        <label htmlFor="validate-transcription-reason" className="block text-xs text-muted-foreground">
          {s('transcriptionRejectionReason')} <span className="text-danger">*</span>
        </label>
        <Select value={rejectionReason} onValueChange={onRejectionReasonChange}>
          <SelectTrigger id="validate-transcription-reason" className="h-11 bg-surface">
            <SelectValue placeholder={s('transcriptionRejectionReason')} />
          </SelectTrigger>
          <SelectContent>
            {TRANSCRIPTION_REJECTION_REASONS.map(([value, key]) => (
              <SelectItem key={value} value={value}>
                {s(key)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="block space-y-1.5 pt-1">
          <span className="text-xs text-muted-foreground">{s('rejectionReasonOptional')}</span>
          <Textarea
            value={detailedRejectionReason}
            onChange={(e) => onDetailedRejectionReasonChange(e.target.value)}
            placeholder={s('rejectionReasonDetailedPlaceholder')}
            className="min-h-[80px]"
          />
        </label>
      </div>
    </section>
  );
}
